// @dipfield/rock: unit conversions. GPL-3.0-or-later.
//
// The Suite and its project file work in kN, kPa, m and degrees (docs/DECISIONS.md, D-006).
// An engine may keep its own units (wedge3d works in MN and MPa, as UnWedge does); the
// conversion then happens here, at the boundary, and nowhere else. IDs: docs/theory.md.
import type { Strength } from "./strength.js";

/** Standard gravity, m/s² (exact by definition). */
export const G0 = 9.80665;

// [UNI-01]
/** kPa → MPa. */
export const kPaToMPa = (kPa: number): number => kPa / 1000;
/** MPa → kPa. */
export const MPaToKPa = (MPa: number): number => MPa * 1000;

/** kN → MN. */
export const kNToMN = (kN: number): number => kN / 1000;
/** MN → kN. */
export const MNToKN = (MN: number): number => MN * 1000;

/** Unit weight kN/m³ → MN/m³. */
export const kNm3ToMNm3 = (kNm3: number): number => kNm3 / 1000;
/** Unit weight MN/m³ → kN/m³. */
export const MNm3ToKNm3 = (MNm3: number): number => MNm3 * 1000;

// [UNI-02]
/** Mass in tonnes → weight in kN (tonne-force). */
export const tonneToKN = (t: number): number => t * G0;
/** Weight in kN → tonne-force. */
export const kNToTonne = (kN: number): number => kN / G0;

/** Density t/m³ (= g/cm³) → unit weight kN/m³. */
export const densityToUnitWeight = (tPerM3: number): number => tPerM3 * G0;

// [UNI-03]
/** The units wedge3d works in, as UnWedge does. The Suite converts every input with the
 *  functions above (and `strengthToMPa`) and every output back, so the engine receives
 *  exactly the numbers the wedge3d app would. */
export const WEDGE3D_UNITS = { force: "MN", stress: "MPa", unitWeight: "MN/m³", length: "m", angle: "degrees" } as const;

/** A shear strength law with stresses in kPa → the same law with stresses in MPa, so that
 *  τ_MPa(σ / 1000) = τ_kPa(σ) / 1000 (STR-01..03). Mohr-Coulomb: c / 1000. Barton-Bandis:
 *  JCS / 1000 (JRC and φr are ratios and angles). Power curve τ = a(σ + c)^b + d:
 *  a·1000^(b−1), c / 1000, d / 1000. */
export function strengthToMPa(s: Strength): Strength {
  switch (s.model) {
    case "mc": return { model: "mc", c: kPaToMPa(s.c), phi: s.phi };
    case "bb": return { model: "bb", jrc: s.jrc, jcs: kPaToMPa(s.jcs), phir: s.phir };
    case "power": return { model: "power", a: s.a * Math.pow(1000, s.b - 1), b: s.b, c: kPaToMPa(s.c), d: kPaToMPa(s.d) };
  }
}
